import React, { ReactNode } from 'react';
import { Form, FormControlProps, SelectPicker, Text } from 'rsuite';

export interface AnnotatedSelectPickerOption {
  label: string;
  value: string;
}

type AnnotatedSelectPickerProps = {
  errorMessage?: string;
  hasError?: boolean;
  label: string;
  options: Array<AnnotatedSelectPickerOption>;
  required?: boolean;
  subtitle?: () => ReactNode;
} & Omit<FormControlProps<string, string>, 'accepter' | 'data'>;

// SelectPicker is generic (SelectPickerProps<T>), which Form.Control's accepter
// overload can't resolve cleanly against FormControlProps. Wrapping it in a
// non-generic component (same pattern as RadioQuestionGroup's
// RadioGroupWithErrorStateAndProps) sidesteps the overload/generic conflict —
// Form.Control still drives it at runtime via name/value/onChange/rule.
const SelectAccepter = ({
  data,
  ...rest
}: {
  data: Array<AnnotatedSelectPickerOption>;
  [key: string]: unknown;
}) => (
  <SelectPicker
    {...rest}
    data={data}
    searchable={false}
    cleanable={false}
  />
);

const AnnotatedSelectPicker = ({
  errorMessage,
  hasError,
  label,
  name,
  onChange,
  options,
  required,
  subtitle = () => undefined,
  value,
  ...rest
}: AnnotatedSelectPickerProps) => {
  const errorId = hasError && name ? `${name}-error` : undefined;

  return (
    <Form.Group controlId={name}>
      <Form.ControlLabel>
        <Text
          as="h5"
          className="text-bold"
        >
          {label}
          {required && ' *'}
        </Text>
        {subtitle && <Text>{subtitle()}</Text>}
      </Form.ControlLabel>
      <Form.Control
        accepter={SelectAccepter}
        data={options}
        name={name}
        onChange={onChange}
        value={value}
        aria-invalid={hasError || undefined}
        aria-describedby={errorId}
        {...rest}
      />
      {hasError && errorMessage && (
        <span
          id={errorId}
          className="text-error"
        >
          {errorMessage}
        </span>
      )}
    </Form.Group>
  );
};

export default AnnotatedSelectPicker;
